<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import menuConfig from '@/json/menu.json';
import { mergeFeatureMenu } from '@/utils/feature-menu';

const store = useStore();
const route = useRoute();
const router = useRouter();

const iconClassMap = {
	'info-center': 'fa fa-laptop',
	dashboard: 'fa fa-laptop',
	analytics: 'fa fa-chart-pie',
	'member-center': 'fa fa-users',
	'finance-center': 'fa fa-wallet',
	'event-center': 'fa fa-calendar',
	'event-website': 'fa fa-globe',
	'event-settings': 'fa fa-sliders',
	'event-invitation': 'fa fa-envelope-open-text',
	'course-events': 'fa fa-calendar',
	'registration-list': 'fa fa-file-lines',
	'registration-fee': 'fa fa-credit-card',
	'checkin-notification': 'fa fa-bell',
	'checkin-management': 'fa fa-clipboard-check',
	'event-survey': 'fa fa-clipboard-list',
	'onsite-interaction': 'fa fa-comments',
	'onsite-questions': 'fa fa-circle-question',
	'badge-quest': 'fa fa-id-badge',
	'session-checkin': 'fa fa-qrcode',
	'question-quiz': 'fa fa-bolt',
	'booth-points': 'fa fa-store',
	'agenda-voting': 'fa fa-square-check',
	'website-center': 'fa fa-globe',
	'publication-center': 'fa fa-book-open',
	'community-center': 'fa fa-envelope',
	'data-center': 'fa fa-chart-bar',
	'my-account': 'fa fa-user-circle',
	settings: 'fa fa-gear',
	organization: 'fa fa-building',
	'sign-out': 'fa fa-right-from-bracket',
};

const normalizeLang = value => {
	if (!value) {
		return 'en';
	}
	const normalized = value.toLowerCase().replace('_', '-');
	const map = {
		'en': 'en',
		'zh-tw': 'zh-tw',
		'zh-hant': 'zh-tw',
		'zh-hk': 'zh-tw',
		'zh-cn': 'zh-cn',
		'zh-hans': 'zh-cn',
		'ja': 'ja',
		'ko': 'ko',
	};
	return map[normalized] || 'en';
};

const lang = computed(() =>
	normalizeLang(store?.state?.settings?.config?.lang)
);

/**
 * Recursively filter out items where visible === false.
 * If a parent is hidden, all children are hidden too.
 */
const filterVisible = items => {
	if (!Array.isArray(items)) return [];
	return items
		.filter(item => item.visible !== false)
		.map(item => {
			if (Array.isArray(item.sub)) {
				const filteredSub = filterVisible(item.sub);
				return {
					...item,
					sub: filteredSub.length > 0 ? filteredSub : undefined,
				};
			}
			return { ...item };
		});
};

const featureAccess = computed(() => store.state.featureAccess);
const ogzuid = computed(() => store.state.settings.auth.accessOUID || '');

const menuItems = computed(() =>
	filterVisible(
		mergeFeatureMenu(
			menuConfig?.Admin ?? [],
			featureAccess.value,
			routeName => router.hasRoute(routeName),
			ogzuid.value
		)
	).map((item, index) => ({
		...item,
		_index: index,
	}))
);

const openGroups = ref({});

const labelFor = item => {
	const labels = item?.label ?? {};
	return (
		labels[lang.value] ||
		labels.en ||
		labels['zh-tw'] ||
		labels['zh-cn'] ||
		''
	);
};

const iconClass = name => iconClassMap[name] || 'fa fa-file-lines';

const isRouteActive = routeName => route?.name === routeName;

/**
 * Recursively check if any leaf route-name in the subtree is currently active.
 */
const isSubtreeActive = item => {
	if (item['route-name']) {
		return isRouteActive(item['route-name']);
	}
	if (Array.isArray(item?.sub)) {
		return item.sub.some(child => isSubtreeActive(child));
	}
	return false;
};

const groupKey = (l1Index, l2Index) =>
	l2Index !== undefined ? `${l1Index}-${l2Index}` : `${l1Index}`;

const isGroupOpen = (l1Index, l2Index) => {
	const key = groupKey(l1Index, l2Index);
	return !!openGroups.value[key];
};

const toggleGroup = (l1Index, l2Index) => {
	const key = groupKey(l1Index, l2Index);
	const isOpen = !!openGroups.value[key];

	if (l2Index === undefined) {
		openGroups.value = isOpen ? {} : { [key]: true };
		return;
	}

	const parentKey = groupKey(l1Index);
	if (isOpen) {
		openGroups.value = { [parentKey]: true };
		return;
	}

	openGroups.value = {
		[parentKey]: true,
		[key]: true,
	};
};

const submenuStyle = isOpen => ({
	display: isOpen ? 'block' : 'none',
});

const syncOpenGroups = () => {
	const next = {};
	menuItems.value.forEach((l1, l1Idx) => {
		if (isSubtreeActive(l1)) {
			next[groupKey(l1Idx)] = true;
			if (Array.isArray(l1.sub)) {
				l1.sub.forEach((l2, l2Idx) => {
					if (isSubtreeActive(l2)) {
						next[groupKey(l1Idx, l2Idx)] = true;
					}
				});
			}
		}
	});
	openGroups.value = next;
};

watch(
	() => route?.name,
	() => {
		syncOpenGroups();
	},
	{ immediate: true }
);

onMounted(() => {
	syncOpenGroups();
});
</script>

<template>
	<div class="menu">
		<template v-for="(l1, l1Idx) in menuItems" :key="l1._index">
			<div v-if="l1Idx > 0" class="menu-divider"></div>
			<div class="menu-header">{{ labelFor(l1) }}</div>

			<template
				v-for="(l2, l2Idx) in l1.sub"
				:key="l2['route-name'] || `${l1Idx}-${l2Idx}`"
			>
				<div
					v-if="l2.sub"
					class="menu-item has-sub"
					:class="{
						active: isSubtreeActive(l2),
						expand: isGroupOpen(l1Idx, l2Idx),
						closed: !isGroupOpen(l1Idx, l2Idx),
					}"
				>
					<a
						href="#"
						class="menu-link"
						@click.prevent="toggleGroup(l1Idx, l2Idx)"
					>
						<span class="menu-icon">
							<i :class="iconClass(l2.icon)"></i>
						</span>
						<span class="menu-text">{{ labelFor(l2) }}</span>
						<span class="menu-caret" aria-hidden="true"
							><b class="caret"></b
						></span>
					</a>
					<div
						class="menu-submenu"
						:style="submenuStyle(isGroupOpen(l1Idx, l2Idx))"
					>
						<template
							v-for="l3 in l2.sub"
							:key="l3['route-name'] || labelFor(l3)"
						>
							<router-link
								v-if="l3['route-name'] && l3.routable !== false"
								:to="{ name: l3['route-name'] }"
								custom
								v-slot="{ navigate, href }"
							>
								<div
									class="menu-item"
									:class="{
										active: isRouteActive(l3['route-name']),
										locked: l3.locked,
									}"
									:title="
										l3.locked
											? $t(
													'feature-access.menu.contact-open' /* 聯絡我們開通 */
											  )
											: ''
									"
								>
									<a
										:href="href"
										class="menu-link"
										:class="{ 'is-locked': l3.locked }"
										@click="navigate"
									>
										<span class="menu-text">{{
											labelFor(l3)
										}}</span>
									</a>
								</div>
							</router-link>
							<div v-else class="menu-item unavailable">
								<span
									class="menu-link is-unavailable"
									aria-disabled="true"
								>
									<span class="menu-text">{{
										labelFor(l3)
									}}</span>
								</span>
							</div>
						</template>
					</div>
				</div>

				<template v-else>
					<router-link
						v-if="l2['route-name'] && l2.routable !== false"
						:to="{ name: l2['route-name'] }"
						custom
						v-slot="{ navigate, href }"
					>
						<div
							class="menu-item"
							:class="{
								active: isRouteActive(l2['route-name']),
								locked: l2.locked,
							}"
							:title="
								l2.locked
									? $t(
											'feature-access.menu.contact-open' /* 聯絡我們開通 */
									  )
									: ''
							"
						>
							<a
								:href="href"
								class="menu-link"
								:class="{ 'is-locked': l2.locked }"
								@click="navigate"
							>
								<span class="menu-icon">
									<i :class="iconClass(l2.icon)"></i>
								</span>
								<span class="menu-text">{{
									labelFor(l2)
								}}</span>
							</a>
						</div>
					</router-link>
					<div v-else class="menu-item unavailable">
						<span
							class="menu-link is-unavailable"
							aria-disabled="true"
						>
							<span class="menu-icon">
								<i :class="iconClass(l2.icon)"></i>
							</span>
							<span class="menu-text">{{ labelFor(l2) }}</span>
						</span>
					</div>
				</template>
			</template>
		</template>
	</div>
</template>
